import React, { useState, useRef, useEffect, useMemo } from "react";
import Pagination from "../utils/pagination/Pagination";
import { Trash2, SquarePen, Eye, Plus, Search, ListRestart } from "lucide-react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  ModalCloseButton,
} from "@chakra-ui/react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "@chakra-ui/react";

// Import category APIs
import { deleteCategory } from "../../../redux/api/admin/collectionApi";
import { getCategoryList } from "../../../redux/reduxApi/admin/CategoryReduxApi";
import SearchCollection from "./SearchCollection";
import { setFilteredStatus } from "../../../redux/reduxSlice/admin/CategorySlice";
import { usePermission } from "../../../../context/PermissionContext";
import { formatDateTime, getFullName } from "../../../../utils/commonFunctions";
import { getEmployeesList } from "../../../redux/reduxApi/admin/EmployeesReduxApi";

function CategoryList() {
  const { canAdd, canEdit, canViewAll, canDelete } = usePermission();

  const [loading, setLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteCategoryId, setDeleteCategoryId] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [recordCount, setRecordCount] = useState(0);


  const { category: categoryList } = useSelector((state) => state.category);
  const isFiltered = useSelector((state) => state.category.isFiltered);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);
  const openTrash = () => setIsOpen(true);
  const closeTrash = () => setIsOpen(false);

  const [employees, setEmployees] = useState([]);
    const employeeMap = useMemo(() => {
      return employees.reduce((map, emp) => {
        const key = emp.ROWID || emp.id || emp.employeeId;
        if (key) {
          map[String(key)] = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Unknown";
        }
        return map;
      }, {});
    }, [employees]);
  
    const getEmployeeDisplayName = (employee) => {
      if (!employee) return "-";
      if (typeof employee === "object") return getFullName(employee);
      const lookup = employeeMap[String(employee)];
      return lookup || String(employee);
    };
  
     useEffect(() => {
        dispatch(getEmployeesList({ page: 1, size: 100, limit: 100 }))
          .then((response) => {
            const employees = response.payload?.data?.employees || [];
            setEmployees(employees);
          })
          .catch((error) => {
            console.error("Error fetching employees for product list:", error);
          });
      }, [dispatch]);

  // Get category List
  useEffect(() => {
    setLoading(true);

    const savedQuery = sessionStorage.getItem("categorySearchQuery");

    if (savedQuery && isFiltered) {
      const queryPayload = {
        ...JSON.parse(savedQuery),
        page: currentPage,
        limit: 30,
      };

      dispatch(getCategoryList(queryPayload))
        .finally(() => setLoading(false));

    } else {
      dispatch(getCategoryList({ page: currentPage, limit: 30 }))
        .finally(() => setLoading(false));
    }

  }, [currentPage, dispatch, isFiltered]);


  useEffect(() => {
    if (categoryList && categoryList.length > 0) {
      setCategoryData(categoryList);
    }
  }, [categoryList]);

  useEffect(() => {
    if (categoryData && categoryData.length > 0) {
      setRecordCount(categoryData.length);
    }
  }, [categoryData]);


  const handleDelete = async () => {
    if (!deleteCategoryId) return;

    setLoading(true);
    try {
      const response = await deleteCategory(deleteCategoryId);
      console.log("Delete response:", response);

      if (response?.status === 200 || response?.data?.success) {
        toast.success("Category deleted successfully");

        // Refresh list
        dispatch(getCategoryList({ page: currentPage, size: 30, limit: 30 }));

        // Reset state
        setDeleteCategoryId("");
        closeTrash();
      } else {
        toast.error("Failed to delete category");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong during deletion");
    }
    setLoading(false);
  };

  const handleDeleteAll = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      await Promise.all(selectedIds.map((id) => deleteCategory(id)));
      toast.success("Selected categories deleted successfully");
      dispatch(getCategoryList({ page: currentPage, size: 30, limit: 30 }));
      setSelectedIds([]);
      closeTrash();
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error("Failed to delete selected categories");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLoading(true);

    dispatch(setFilteredStatus(false));
    sessionStorage.removeItem("categorySearchQuery");

    dispatch(getCategoryList({ page: 1, limit: 30 }))
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>
      )}

      <Modal isOpen={isOpen} onClose={closeTrash}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to Delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedIds.length > 0
              ? `This will delete ${selectedIds.length} selected categories. This action cannot be undone.`
              : "This action cannot be undone."}
          </ModalBody>
          <ModalFooter>
            <Button
              className="main-btn"
              mr={3}
              onClick={() => {
                if (selectedIds.length > 0) {
                  handleDeleteAll();
                } else {
                  handleDelete();
                }
              }}
              isLoading={loading}
            >
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeTrash}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="flex flex-col min-h-screen relative">
        <div className="d-flex justify-content-between mb-1 px-2">
          <span>
            {selectedIds.length > 0 && (
              <button className="main-btn mx-2" onClick={openTrash}>
                <span className="inner-content">
                  <Trash2 size="18px" />
                  <span className="ms-1">
                    Delete ({selectedIds.length})
                  </span>
                </span>
              </button>
            )}
          </span>

          <div className="flex gap-1">
            <button
              className="main-btn mx-1"
              onClick={isFiltered ? handleReset : openSearch}
            >
              <span className="inner-content">
                {isFiltered ? (
                  <>
                    <ListRestart size="18px" />
                    <span className="ms-1">Show All</span>
                  </>
                ) : (
                  <>
                    <Search size="18px" />
                    <span className="ms-1">Search</span>
                  </>
                )}
              </span>
            </button>
            {/* <Link to="/admin/collection-form">
              <button className="d-flex justify-content-between align-items-center main-btn mx-2">
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Create Category</span>
                </span>
              </button>
            </Link> */}

            {/* <button
  onClick={() => canAdd("category") ? navigate("/admin/collection-form") : ""}
  className="d-flex justify-content-between align-items-center main-btn mx-2"
>
  <span className="inner-content">
    <Plus size="18px" />
    <span className="ms-1">Create Category</span>
  </span>
</button> */}

            {canAdd("category") && (
              <button
                onClick={() => navigate("/admin/inventory/collection-form")}
                className="d-flex justify-content-between align-items-center main-btn mx-2"
              >
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Create Category</span>
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="flex-grow px-1">
          <TableContainer
            className="rounded-lg"
            style={{
              height: "calc(100vh - 160px)",
              backgroundColor: "#fdfdfd",
              border: "1px solid #e7e7e7",
              overflowX: "auto",
              overflowY: "auto",
            }}
          >
            <Table variant="simple" size="sm">
              <Thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "white",
                }}
              >
                <Tr>
                  <Th width="50px">
                    <Checkbox
                      isChecked={
                        selectedIds.length === categoryList.length &&
                        categoryList.length > 0
                      }
                      isIndeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < categoryList.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(
                            categoryList.map((item) => item.ROWID)
                          );
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                  </Th>
                  <Th>Actions</Th>
                  <Th>Category Name</Th>
                  <Th>Description</Th>
                  <Th>HSC Code</Th>
                  <Th>Daily Production</Th>
                  <Th>Tax %</Th>
                  <Th>Parent Category</Th>
                  <Th>Created By</Th>
                  <Th>Modified By</Th>
                  <Th>Created Time</Th>
                  <Th>Modified Time</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categoryData?.map((item, index) => (
                  <Tr
                    key={index}
                    _hover={{ bg: "gray.50" }}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate("/admin/inventory/collection-detail", {
                        state: { categoryData: item },
                      })
                    }
                  >
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={selectedIds.includes(item.ROWID)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds((prev) => [...prev, item.ROWID]);
                          } else {
                            setSelectedIds((prev) =>
                              prev.filter((id) => id !== item.ROWID)
                            );
                          }
                        }}
                      />
                    </Td>
                    <Td>
                      <div className="d-flex gap-1">
                        {/* <Eye
                          color="#011638"
                          size="16px"
                          strokeWidth="1"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/admin/collection-detail", {
                              state: { categoryData: item },
                            });
                          }}
                        /> */}
                        {/* <Eye
  color="#011638"
  size="16px"
  strokeWidth="1"
  style={{ cursor: "pointer" }}
  onClick={(e) => {
    e.stopPropagation();
    return canViewAll("category") ? navigate("/admin/collection-detail", {
      state: { categoryData: item },
    }) : "";
  }}
/> */}

                        {canViewAll("category") && (
                          <Eye
                            color="#011638"
                            size="16px"
                            strokeWidth="1"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/admin/inventory/collection-detail", {
                                state: { categoryData: item },
                              });
                            }}
                          />
                        )}
                        {/* <SquarePen
                          color="#011638"
                          size="16px"
                          strokeWidth="1"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/admin/collection-form", {
                              state: {
                                id: item.ROWID,
                                isUpdateMode: true,
                              },
                            });
                          }}
                        /> */}
                        {/* <SquarePen
  color="#011638"
  size="16px"
  strokeWidth="1"
  style={{ cursor: "pointer" }}
  onClick={(e) => {
    e.stopPropagation();
    return canEdit("category") ? navigate("/admin/collection-form", {
      state: {
        id: item.ROWID,
        isUpdateMode: true,
      },
    }) : "";
  }}
/> */}

                        {canEdit("category") && (
                          <SquarePen
                            color="#011638"
                            size="16px"
                            strokeWidth="1"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/admin/inventory/collection-form", {
                                state: {
                                  id: item.ROWID,
                                  isUpdateMode: true,
                                },
                              });
                            }}
                          />
                        )}
                        {/* <Trash2
                          color="#011638"
                          size="16px"
                          strokeWidth="1"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openTrash();
                            setDeleteCategoryId(item?.ROWID);
                          }}
                        /> */}
                        {/* <Trash2
  color="#011638"
  size="16px"
  strokeWidth="1"
  style={{ cursor: "pointer" }}
  onClick={(e) => {
    e.stopPropagation();
    return canDelete("category") ? (openTrash(), setDeleteCategoryId(item?.ROWID)) : "";
  }}
/> */}

                        {canDelete("category") && (
                          <Trash2
                            color="#011638"
                            size="16px"
                            strokeWidth="1"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openTrash();
                              setDeleteCategoryId(item?.ROWID);
                            }}
                          />
                        )}
                      </div>
                    </Td>
                    <Td>{item?.name || "N/A"}</Td>
                    <Td>{item?.description || "N/A"}</Td>
                    <Td>{item?.hscCode || "N/A"}</Td>
                    <Td>{item?.dailyProduction || "N/A"}</Td>
                    <Td>{item?.tax || "N/A"}</Td>
                    <Td>{item?.parentCategoryName || "None"}</Td>
                    {/* <Td>{new Date(item?.CREATEDTIME).toLocaleDateString()}</Td> */}
                   <Td className="font-colors">{getEmployeeDisplayName(item?.createdByUser || item?.createdByDetails || item?.createdBy)}</Td>
                                          <Td className="font-colors">{getEmployeeDisplayName(item?.modifiedByUser || item?.modifiedByDetails || item?.modifiedBy)}</Td>
                                          <Td className="font-colors">{formatDateTime(item?.CREATEDTIME)}</Td>
                                          <Td className="font-colors">{formatDateTime(item?.MODIFIEDTIME)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        {/* Footer */}
        <div
          className="w-100 bg-white shadow-sm"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 20,
          }}
        >
          <div className="row mx-2 d-flex align-items-center py-2">
            <div className="col-lg-3 col-md-6 col-12">
              <p className="fw-semibold mb-0">
                Total Categories: {categoryList?.length || 0}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12 d-flex justify-content-center">
              {/* Add pagination if needed */}
              <Pagination totalPages={Math.ceil(recordCount / 30)} onPageChange={setCurrentPage} />

            </div>
          </div>
        </div>
      </div>

      <SearchCollection
        closeDrawer={closeSearch}
        isSearchDrawerOpen={isSearchOpen}
        onSearchResults={(filteredData) => {
        }}
      />
    </>
  );
}

export default CategoryList;